import React, { useState, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import "./CustomDialogForEditLogs.css"; // Custom styles for the dialog

const CustomDialogForEditLogs = ({
    isOpen,
    onClose,
    editLogsData,
}) => {
    const [filteredLogs, setFilteredLogs] = useState([]); // Array to hold filtered logs
    const [selectedInvoice, setSelectedInvoice] = useState(""); // Track the currently selected invoice
    const [columnDefs, setColumnDefs] = useState([]); // Column definitions for Ag-Grid

    // Initialize with the first invoice's data
    useEffect(() => {
        if (editLogsData.length > 0) {
            const firstInvoice = editLogsData[0].invoice_no;
            handleInvoiceChange(firstInvoice);
            setSelectedInvoice(firstInvoice);
        }
    }, [editLogsData]);

    // Handle invoice selection
    const handleInvoiceChange = (invoiceNo) => {
        setSelectedInvoice(invoiceNo); // Update selected invoice
        // Filter logs for the selected invoice
        const logsForInvoice = editLogsData.filter((log) => log.invoice_no === invoiceNo);
    
        // Prepare row data for Ag-Grid
        const rowData = [];
        logsForInvoice.forEach((log) => {
            Object.keys(log.updated_data)
                .filter((key) => !key.endsWith("_modified")) // Exclude keys ending with "_modified"
                .forEach((key) => {
                    const isDateField = key === "eway_bill_expiry_date"; // Check if the field is eway_bill_expiry_date
                    rowData.push({
                        field: keyDisplayMap[key] || key,
                        existing_data: isDateField
                            ? log.existing_data[key]
                                ? formatDate(log.existing_data[key]) // Format date if it's a date field and not empty
                                : "" // Show empty if no data
                            : log.existing_data[key],
                        updated_data: isDateField
                            ? log.updated_data[key]
                                ? formatDate(log.updated_data[key]) // Format date if it's a date field and not empty
                                : "" // Show empty if no data
                            : log.updated_data[key],
                        updated_time: log.updated_time
                            ? formatDate(log.updated_time) // Always format updated_time if not empty
                            : "", // Show empty if no data
                        updated_by: log.updated_by? log.updated_by: ""
                    });
                });
        });
    
        // Update the filtered logs and column definitions for Ag-Grid
        setFilteredLogs(rowData);
        setColumnDefs([
            { headerName: "Field", field: "field", sortable: true, filter: true },
            { headerName: "Existing Data", field: "existing_data", sortable: true, filter: true },
            { headerName: "Updated Data", field: "updated_data", sortable: true, filter: true },
            { headerName: "Updated Time", field: "updated_time", sortable: true, filter: true },
            { headerName: "Updated By", field: "updated_by", sortable: true, filter: true },
        ]);
    };
    
    

    // Format date and time
    const formatDate = (dateString) => {
        const date = new Date(dateString); // Parse the UTC date
        const day = String(date.getUTCDate()).padStart(2, "0");
        const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // Months are 0-indexed
        const year = date.getUTCFullYear();
        const hours = String(date.getUTCHours()).padStart(2, "0");
        const minutes = String(date.getUTCMinutes()).padStart(2, "0");
        return `${day}-${month}-${year} ${hours}:${minutes}`;
    };

    // Mapping for display names
    const keyDisplayMap = {
        tracking_type: "Tracking Mode",
        sim_consent_status: "SIM Consent Status",
        lr_no: "LR No",
        invoice_no: "Invoice No",
        truck_no: "Vehicle No",
        status: "Status",
        reason_for_delay: "Reason For Delay",
        invoice_time: "Invoice Date",
        dispatch_date: "Dispatch Date",
        customer_name: "Consignee Name",
        truck_seal_entity: "Entity",
        invoice_type: "Invoice Type",
        transporter_name: "Transporter",
        consigner_city: "Source City",
        consigner_pincode: "Source Pincode",
        consigner_zone: "Source Zone",
        consignee_city: "Destination City",
        consignee_pincode: "Destination Pincode",
        consignee_state: "Destination State",
        consignee_zone: "Destination Zone",
        driver_mobile_no: "Mobile Number",
        movement_type: "Movement Type",
        total_weight: "Total Weight",
        total_cbm: "Total CBM",
        tpt_category: "Transporter Category",
        truck_type: "Vehicle Type",
        truck_seal_no: "Truck Seal No",
        origin_destination_area: "ODA",
        transit_time: "TAT",
        last_location: "Event Location",
        event_date_time: "Event Date & Time",
        last_process_on: "Last Data Received On",
        expected_trip_end: "Expected ETA",
        revised_trip_end: "Revised Estimated Delivery Date",
        ontime_status: "Performance",
        eway_bill_no: "E-Way Bill No",
        shipment_mode: "Mode",
        fuel_type: "Fuel Type",
        no_of_boxes: "No Of Boxes",
        remarks: "Reason",
        vehicle_capacity: "Vehicle Capacity",
        eway_bill_expiry_date: "E-Way Bill Expiry Date"
    };

    // Get unique invoices for the dropdown
    const uniqueInvoices = Array.from(new Set(editLogsData.map((log) => log.invoice_no)));

    // Render dialog only if it's open
    if (!isOpen) return null;

    return (
        <div className="dialog-overlay" onClick={onClose}>
            <div className="dialog-content1" onClick={(e) => e.stopPropagation()}>
                {/* Dialog Header */}
                <div className="dialog-header">
                    <h2>Edit Logs</h2>
                </div>

                {/* Dialog Body */}
                <div className="dialog-body">
                    {/* Dropdown to select invoice */}
                    <div>
                        <label htmlFor="invoice-select">Select Invoice:</label>
                        <select
                            id="invoice-select"
                            onChange={(e) => handleInvoiceChange(e.target.value)}
                            value={selectedInvoice}
                        >
                            
                            {uniqueInvoices.map((invoiceNo, index) => (
                                <option key={index} value={invoiceNo}>
                                    {invoiceNo}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Display grid with filtered log */}
                    <div
                        className="ag-theme-balham mt-3"
                        style={{ 
                            maxHeight: "400px",width: "100%", overflow: "auto" }}
                    >
                        <AgGridReact
                            columnDefs={columnDefs}
                            rowData={filteredLogs}
                            defaultColDef={{
                                sortable: true,
                                filter: true,
                                resizable: true,
                            }}
                            domLayout="autoHeight"
                            pagination={true}
                            paginationPageSize={50}
                            enableRangeSelection={true}
                            enableCellContextMenu={true}
                        />
                    </div>
                </div>

                {/* Dialog Actions */}
                <div className="dialog-actions">
                    <button className="btn-primary" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CustomDialogForEditLogs;
